// The in-memory twin of MEMBER-ENTRY (founder-approved 2026-10-21): `GET me/numbers` and the
// holder's write scope `POST observations` checks (fake.ts). Over the structure block's stores
// (seats, assignments, metrics, seat metric sets) and the shared observation rows, with the API's
// rules (services/api/src/kpi/self-report.ts): the seats the caller's person membership holds
// that no DE also holds, and on each the manual/import, client-visible, unarchived metrics of its
// set tracked ≠ off; per metric the last ended period while it has no value (`late`), else the
// current one (`due` / `entered`); the due day is the period's last working day. Weeks and
// months only (the twin's scorecard is weekly).
//
// Seed (on the holder's first read, `seedMemberNumbers`, so other personas keep the generated
// data): Acme's Recruiting lead seat (Jordan Lee, the "Acme seat holder" persona) carries two
// manual weekly KPIs, "Client calls" (last week entered: due this Friday) and "Hiring-manager
// meetings" (last week empty: late), with weekly goals of the twin's own.
import { isoWeekKey } from "../focus/focus";
import { shiftWeek, weekStart } from "../focus/weeks";
import type { Principal } from "./fake";
import { COMPANY_IDS, type FakeCompany, uid } from "./fake-data";
import { observationRow, periodTypeOf } from "./fake-entry";
import type { MetricDef, Observation, Seat, SeatAssignment } from "./types";

export const RECRUITING_LEAD_SEAT = uid(0x2000, 0xa11);
export const JORDAN = uid(0x1000, 0xa11);
const EPOCH = "2026-06-01T09:00:00Z";

type SetLine = {
  metric_def_id: string;
  kpi_weight: string;
  goal_weight: string;
  tracking_mode: "off" | "context" | "scored";
  penalty_only: boolean;
  position: number;
};

/** The seeded member KPIs: [metric, weekly goal]. */
const SEEDED: { key: string; name: string; n: number; goal: number }[] = [
  { key: "lead.client_calls", name: "Client calls", n: 0x51, goal: 15 },
  { key: "lead.hm_meetings", name: "Hiring-manager meetings", n: 0x52, goal: 4 },
];
const metricId = (n: number) => uid(0x3a00, n);

/** Acme's member KPI definitions (appended to the structure block's metric store). */
export function memberMetricDefs(company: FakeCompany): MetricDef[] {
  if (company.id !== COMPANY_IDS.acme) return [];
  return SEEDED.map((m) => ({
    company_id: company.id,
    id: metricId(m.n),
    key: m.key,
    name: m.name,
    description: null,
    unit: "number",
    direction: "gte",
    aggregation: "cumulative",
    normalization: null,
    cadence: "weekly",
    source: "manual",
    event_query: null,
    formula: null,
    task_query: null,
    visibility: "client",
    gap_policy: "excuse",
    // AGENT-REPORTING: a person's own numbers need no review.
    needs_review: false,
    archived_at: null,
    bands: null,
    gate: null,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: EPOCH,
    updated_at: EPOCH,
  }));
}

/** The Recruiting lead seat's metric set (the structure block's seat metric store). */
export function memberSeatSets(): [string, SetLine[]][] {
  return [
    [
      RECRUITING_LEAD_SEAT,
      SEEDED.map((m, position) => ({
        metric_def_id: metricId(m.n),
        kpi_weight: "1",
        goal_weight: "0",
        tracking_mode: "scored" as const,
        penalty_only: false,
        position,
      })),
    ],
  ];
}

/** Last week's "Client calls" (so it is due, not late) and two weeks of history. */
export function memberSeedObservations(now: Date, next: () => number): Observation[] {
  const week = isoWeekKey(now);
  const calls = metricId(0x51);
  const meetings = metricId(0x52);
  const rows: [string, string, number][] = [
    [calls, shiftWeek(week, -1), 12],
    [calls, shiftWeek(week, -2), 17],
    [calls, shiftWeek(week, -3), 14],
    [meetings, shiftWeek(week, -2), 5],
    [meetings, shiftWeek(week, -3), 3],
  ];
  return rows.map(([metric, period, value]) => ({
    ...observationRow(COMPANY_IDS.acme, next(), {
      metric_def_id: metric,
      period_type: "week",
      period_key: period,
      value,
      note: null,
      subject: { kind: "seat", id: RECRUITING_LEAD_SEAT },
    }),
    entered_by_membership_id: JORDAN,
    actor_kind: "person" as const,
    actor_membership_id: JORDAN,
  }));
}

export type NumbersStores = {
  seatsOf: (company: FakeCompany) => Seat[];
  metricsOf: (company: FakeCompany) => MetricDef[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  seatMetricsOf: (seatId: string) => SetLine[];
  observations: Observation[];
  liveOf: (rows: Observation[]) => Observation | null;
  now: () => Date;
};

/** The caller's person membership in the company, or null (an operator, a viewer by grant). */
export function personOf(p: Principal): string | null {
  return (
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person")
      ?.membership_id ?? null
  );
}

/** The holder's scope: seat → the metrics it may enter (the API's `selfReportScope`). */
export function selfScope(s: NumbersStores, p: Principal): Map<string, MetricDef[]> {
  const me = personOf(p);
  const out = new Map<string, MetricDef[]>();
  if (!me) return out;
  const active = s.assignmentsOf(p.company).filter((a) => a.active_to === null);
  const defs = new Map(s.metricsOf(p.company).map((m) => [m.id, m]));
  for (const seat of s.seatsOf(p.company)) {
    if (seat.status === "closed" || seat.status === "proposed") continue;
    const on = active.filter((a) => a.seat_id === seat.id);
    if (!on.some((a) => a.membership_id === me && a.membership_kind === "person")) continue;
    if (on.some((a) => a.membership_kind !== "person")) continue;
    out.set(
      seat.id,
      [...s.seatMetricsOf(seat.id)]
        .sort((a, b) => a.position - b.position)
        .flatMap((l) => {
          const d = defs.get(l.metric_def_id);
          return d &&
            l.tracking_mode !== "off" &&
            (d.source === "manual" || d.source === "import") &&
            d.visibility === "client" &&
            !d.archived_at &&
            d.cadence
            ? [d]
            : [];
        }),
    );
  }
  return out;
}

const monthKey = (d: Date) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
const shiftMonth = (key: string, delta: number) => {
  const [y, m] = key.split("-").map(Number) as [number, number];
  return monthKey(new Date(Date.UTC(y, m - 1 + delta, 1)));
};
const lastWorkday = (lastDay: Date) => {
  const d = new Date(lastDay);
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const dueOn = (type: string, key: string) => {
  if (type === "week") return lastWorkday(new Date(weekStart(key).getTime() + 6 * 86_400_000));
  const [y, m] = key.split("-").map(Number) as [number, number];
  return lastWorkday(new Date(Date.UTC(y, m, 0)));
};

/** `GET me/numbers` (header). */
export function myNumbers(s: NumbersStores, p: Principal) {
  const scope = selfScope(s, p);
  const name = p.me.user.name || p.me.user.email;
  const seats = s.seatsOf(p.company);
  const now = s.now();
  return {
    currency: "USD",
    seats: [...scope].map(([seatId, defs]) => {
      const seat = seats.find((x) => x.id === seatId);
      return {
        seat_id: seatId,
        title: seat?.title ?? "Seat",
        holder_name: name,
        kpi_visible: true,
        items: defs.flatMap((d) => {
          const type = periodTypeOf(d.cadence);
          if (type !== "week" && type !== "month") return [];
          const current = type === "week" ? isoWeekKey(now) : monthKey(now);
          const recent = Array.from({ length: 6 }, (_, i) =>
            type === "week" ? shiftWeek(current, -i) : shiftMonth(current, -i),
          );
          const valueIn = (key: string) => {
            const cell = s.observations.filter(
              (o) =>
                o.company_id === p.company.id &&
                o.metric_def_id === d.id &&
                o.subject_kind === "seat" &&
                o.subject_id === seatId &&
                o.period_key === key,
            );
            const live = cell.length ? s.liveOf(cell) : null;
            return live?.value == null ? null : { id: live.id, value: Number(live.value) };
          };
          const late = valueIn(recent[1] as string) === null;
          const key = late ? (recent[1] as string) : current;
          const row = late ? null : valueIn(current);
          return [
            {
              metric_def_id: d.id,
              key: d.key,
              name: d.name,
              unit: d.unit,
              direction: d.direction,
              source: d.source as "manual" | "import",
              cadence: d.cadence as NonNullable<MetricDef["cadence"]>,
              period_type: type,
              period_key: key,
              due_on: dueOn(type, key),
              status: late ? ("late" as const) : row ? ("entered" as const) : ("due" as const),
              value: row?.value ?? null,
              observation_id: row?.id ?? null,
              target: SEEDED.find((m) => metricId(m.n) === d.id)?.goal ?? null,
              recent: recent.map((k) => ({ period_key: k, value: valueIn(k)?.value ?? null })),
            },
          ];
        }),
      };
    }),
  };
}
